import React, { useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { AnyStyledComponent, css } from 'styled-components';
import { Flex } from '@/ui/Flex/Flex';
import z from 'zod';
import { T } from '@/ui/Text/Text';
import { FileConfig } from 'src/const/appConfig';
import {
  doesFileSizeExceedMaximum,
  doesFileResolutionExceedMaximum,
} from 'src/utils/fileUtils';
import { TOASTTYPE, useToast } from '../Toast/Toast';
import IconUpload from './IconUpload.svg';
import { LazyLoadingFile, Loader } from './LazyLoadingFile';

type StyledProps = {
  readonly isOver: boolean;
  readonly hasFile: boolean;
};

const StyledIconUpload = styled(IconUpload as AnyStyledComponent)<{
  readonly $isOver: StyledProps['isOver'];
}>`
  ${({ $isOver, theme }) => css`
    path {
      stroke: ${$isOver ? theme.color('#239AE7') : '#282828'};
    }
  `};
`;

const StyledContainer = styled.div<StyledProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  min-height: 100px;
  text-align: center;
  flex-direction: column;
  cursor: pointer;

  ${({ isOver, theme, hasFile }) => css`
    border: 2px dashed ${isOver ? theme.color('#239AE7') : '#000000'};
    color: ${isOver ? theme.color('#239AE7') : '#282828'};

    ${hasFile
      ? css`
          margin: 0 0;
          border: none;
          padding: 0;
        `
      : css`
          padding: 8px;
        `}
  `};
`;

const StyledMedia = css`
  max-height: 180px;
  max-width: 100%;
`;

export const StyledImage = styled.img`
  ${StyledMedia}
`;

export const StyledVideo = styled.video`
  ${StyledMedia}
`;

export const ONE_MB_IN_BYTES = 1048576;

export const FilePropsSchema = z.object({
  slideId: z.string().optional(),
  contentType: z.string(),
  name: z.string(),
  size: z.number().optional(),
  asset: z.string().optional(),
  file: z
    .union([z.instanceof(File), z.instanceof(Blob), z.string()])
    .optional(),
});

export type FileProps = z.infer<typeof FilePropsSchema>;

type Props = {
  readonly file: FileProps | undefined;
  readonly setFiles: (files: readonly FileProps[]) => void;
  readonly lazyLoading?: boolean;
};

export const DropFile = ({ file, setFiles, lazyLoading }: Props) => {
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [isOver, setIsOver] = useState(false);
  const toast = useToast();
  const intl = useIntl();

  const handleLoad = () => {
    setMediaLoaded(true);
  };

  const validator = (fileToValidate: File) => {
    if (
      !fileToValidate.type.includes('image') &&
      !fileToValidate.type.includes('video')
    ) {
      return [
        {
          code: 'file-invalid-type',
          message: 'File is invalid',
        },
      ];
    }

    if (doesFileSizeExceedMaximum(fileToValidate)) {
      const maxFileSize = fileToValidate.type.includes('image')
        ? FileConfig.MAX_IMAGE_SIZE_BYTES
        : FileConfig.MAX_VIDEO_SIZE_BYTES;
      return [
        {
          code: 'file-too-large',
          message: `Max file size is ${(maxFileSize / ONE_MB_IN_BYTES).toFixed(
            0,
          )}MB`,
        },
      ];
    }

    return null;
  };

  // eslint-disable-next-line consistent-return
  const processFile = async (acceptedFile: File) => {
    const allowedExtensions = ['.mp4', '.png', '.jpg', '.jpeg'];
    const fileExtension = acceptedFile.name.split('.').pop()?.toLowerCase();

    if (!fileExtension || !allowedExtensions.includes(`.${fileExtension}`)) {
      return toast({
        title: intl.formatMessage({
          defaultMessage: 'Invalid file type.',
          id: 'FNFmPG',
        }),
        subtitle: intl.formatMessage({
          defaultMessage:
            'Only .mp4, .png, .jpg, and .jpeg files are allowed. 🫤',
          id: 'burqlF',
        }),
        mode: TOASTTYPE.Warn,
      });
    }
    const fileReader = new FileReader();

    fileReader.onload = async (event) => {
      if (event.target && event.target.result instanceof ArrayBuffer) {
        const arrayBuffer = event.target.result;
        const chunkOffsets = Array.from(
          { length: Math.ceil(arrayBuffer.byteLength / ONE_MB_IN_BYTES) },
          (_, index) => index * ONE_MB_IN_BYTES,
        );

        const chunks: readonly Uint8Array[] = chunkOffsets.map((offset) => {
          const chunk = new Uint8Array(
            arrayBuffer.slice(offset, offset + ONE_MB_IN_BYTES),
          );
          return chunk;
        });

        const fileData = new Blob(chunks.slice(), {
          type: acceptedFile.type,
        });
        const assetUrl = URL.createObjectURL(fileData);

        const readFile = {
          contentType: acceptedFile.type,
          name: acceptedFile.name,
          size: acceptedFile.size,
          asset: assetUrl,
          file: fileData,
        };

        setFiles([readFile]);
        return null;
      }
      setFiles([]);
      return null;
    };

    fileReader.readAsArrayBuffer(acceptedFile);
  };

  const onDrop = (
    acceptedFiles: readonly File[],
    rejectedFiles: readonly FileRejection[],
  ) => {
    rejectedFiles?.forEach((rejectedFile) => {
      rejectedFile.errors.forEach((fileError) => {
        switch (fileError.code) {
          case 'file-too-large':
            toast({
              title: intl.formatMessage({
                defaultMessage: 'The file you provided is too large.',
                id: 'oCApuQ',
              }),
              mode: TOASTTYPE.Danger,
            });
            return false;
          case 'file-invalid-type':
            toast({
              title: intl.formatMessage({
                defaultMessage: 'File is invalid',
                id: '3765ts',
              }),
              mode: TOASTTYPE.Danger,
            });
            return false;
          default:
            toast({
              title: intl.formatMessage({
                defaultMessage: 'An error occurred while uploading the file.',
                id: 'xomPoH',
              }),
              mode: TOASTTYPE.Danger,
            });
            return false;
        }
      });
    });

    setIsOver(false);
    acceptedFiles.forEach(async (acceptedFile: File) => {
      // max resolution check can't be done in valiadtor of the dropzone, because it is an asynchorn function
      if (!(await doesFileResolutionExceedMaximum(acceptedFile))) {
        return processFile(acceptedFile);
      }
      toast({
        title: intl.formatMessage({
          defaultMessage: 'The file you provided has too high resolution.',
          id: 'aqIDyE',
        }),
        mode: TOASTTYPE.Danger,
      });

      return false;
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'video/mp4': ['.mp4'],
    },
    onDragEnter: () => setIsOver(true),
    onDragLeave: () => setIsOver(false),
    onDrop,
    validator,
  });

  const getFileComponent = (fileData: FileProps) => {
    const isImage = fileData.contentType?.includes('image');
    if (lazyLoading) {
      return <LazyLoadingFile isImage={isImage} file={fileData} />;
    }

    return (
      <>
        {!mediaLoaded && <Loader />}
        {isImage ? (
          <StyledImage
            alt={fileData.name}
            src={fileData.asset}
            onLoad={handleLoad}
            style={{
              opacity: mediaLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          />
        ) : (
          <StyledVideo
            onLoadedData={handleLoad}
            style={{
              opacity: mediaLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
            autoPlay
            muted
            loop
          >
            <source src={fileData.asset} type={fileData.contentType} />
          </StyledVideo>
        )}
      </>
    );
  };

  return (
    <StyledContainer
      {...getRootProps()}
      hasFile={Boolean(file)}
      isOver={isOver}
    >
      {file ? (
        getFileComponent(file)
      ) : (
        <>
          <input {...getInputProps()} />
          <Flex align="center" direction="column" gap={8}>
            <StyledIconUpload $isOver={isOver} />

            {isOver ? (
              <T body2 bold>
                <FormattedMessage defaultMessage="Drop file here" id="mR/SkK" />
              </T>
            ) : (
              <T body2 bold>
                <T underline>
                  <FormattedMessage
                    defaultMessage="Choose a file"
                    id="h/ftND"
                  />
                </T>
                <br />
                <FormattedMessage
                  defaultMessage=" or drag it here"
                  id="4z4yAq"
                />
              </T>
            )}
          </Flex>
        </>
      )}
    </StyledContainer>
  );
};
